import { useState } from 'react';
import { newId, removeSheetOps, sheetsInOrder, type Project, type SheetKind } from '@elec/domain';
import type { Editor } from '@elec/editor';
import type { MessageKey, Translate } from '@elec/i18n';
import { Icon, Menu } from './ui.tsx';

const KINDS: SheetKind[] = ['single-line', 'multi-line', 'control', 'position-plan'];

export function SheetTabs(props: {
  editor: Editor;
  project: Project;
  t: Translate;
  confirm(r: { title: string; body: string; confirm: string; danger?: boolean }): Promise<boolean>;
  notify(msg: string): void;
}) {
  const { editor, project, t } = props;
  const sheets = sheetsInOrder(project);
  const [renaming, setRenaming] = useState<string | null>(null);

  const addSheet = (kind: SheetKind) => {
    const id = newId();
    const base = t(`sheetKind.${kind}` as MessageKey);
    const n = sheets.filter((s) => s.kind === kind).length;
    const current = project.sheets[editor.sheetId];
    editor.history.dispatch({
      label: t('tx.sheetAdd'),
      ops: [
        {
          op: 'add',
          coll: 'sheets',
          value: {
            id,
            name: n ? `${base} ${n + 1}` : base,
            kind,
            paper: current?.paper ?? 'A4',
            orientation: current?.orientation ?? 'landscape',
            order: Math.max(...sheets.map((s) => s.order)) + 1,
          },
        },
      ],
    });
    editor.setSheet(id);
  };

  const deleteSheet = async (id: string) => {
    const sheet = project.sheets[id];
    if (!sheet) return;
    if (sheets.length <= 1) return props.notify(t('sheet.lastOne'));
    const ok = await props.confirm({
      title: t('sheet.delete'),
      body: t('sheet.confirmDelete', { name: sheet.name }),
      confirm: t('dialog.delete'),
      danger: true,
    });
    if (!ok) return;
    const idx = sheets.findIndex((s) => s.id === id);
    const next = sheets[idx + 1] ?? sheets[idx - 1]!;
    editor.setSheet(next.id);
    editor.history.dispatch({ label: t('tx.sheetDelete'), ops: removeSheetOps(editor.project, id) });
  };

  const rename = (id: string, name: string) => {
    setRenaming(null);
    const n = name.trim();
    if (n && n !== project.sheets[id]?.name)
      editor.update([{ op: 'update', coll: 'sheets', id, patch: { name: n } }]);
  };

  return (
    <div className="sheet-tabs">
      <div className="sheet-tablist" role="tablist" aria-label={t('sheet.tabs')}>
        {sheets.map((s, i) => {
          const active = s.id === editor.sheetId;
          return (
            <div key={s.id} className={`tab${active ? ' active' : ''}`}>
              {renaming === s.id ? (
                <input
                  autoFocus
                  defaultValue={s.name}
                  onBlur={(e) => rename(s.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                />
              ) : (
                <button
                  role="tab"
                  aria-selected={active}
                  onClick={() => editor.setSheet(s.id)}
                  onDoubleClick={() => setRenaming(s.id)}
                  title={t(`sheetKind.${s.kind}` as MessageKey)}
                  data-testid={`sheet-tab-${i}`}
                >
                  <span className="tab-no">{i + 1}</span>
                  {s.name}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {/* The open sheet's menu, after the tabs (a tab list holds tabs only). */}
      {renaming !== editor.sheetId && (
        <Menu
          trigger={<Icon name="more" size={14} />}
          label={t('sheet.menu', { name: sheets.find((s) => s.id === editor.sheetId)?.name ?? '' })}
          testId="sheet-menu"
          items={[
            { label: t('sheet.rename'), onSelect: () => setRenaming(editor.sheetId) },
            {
              label: t('sheet.delete'),
              onSelect: () => void deleteSheet(editor.sheetId),
              danger: true,
              disabled: sheets.length <= 1,
            },
          ]}
        />
      )}
      <Menu
        label={t('sheet.add')}
        trigger={
          <span className="add-sheet" title={t('sheet.add')}>
            <Icon name="plus" size={14} />
          </span>
        }
        testId="add-sheet"
        items={KINDS.map((k) => ({
          label: t(`sheetKind.${k}` as MessageKey),
          onSelect: () => addSheet(k),
          testId: `add-sheet-${k}`,
        }))}
      />
    </div>
  );
}
